/**
 * The consumer's self-funding access and the one route guard built on it.
 *
 * A consumer who never paid has no subscription row (`clientStripe` is null). A row that no longer
 * gives access (an expired 365-day period, a lapsed older plan) means access expired: the paywall
 * offers renewal and every saved account is still there.
 *
 * `resolveSelfFundRoute` is the single guard for the paid self-funding route family. It covers the
 * D2C self-funding steps (blocked unless access is active) and the legacy paid onboarding and guided
 * pages a consumer-flow client may still open from an old bookmark (always sent back into the
 * consumer flow, which no longer uses them). Advisors and wizards are never redirected. This is
 * product gating in the browser, not API security: the APIs behind these pages are not entitlement-
 * checked.
 */
import { consumerEntryPath, consumerStepPath } from './paths';
import {
  FIRST_SELF_FUND_SEQUENCE_STEP,
  isSelfFundSequenceStep,
  type ConsumerStepId,
} from './steps';

export type SelfFundAccess = 'none' | 'active' | 'expired';

export function getSelfFundAccess(
  clientStripe: ClientStripeSession | null | undefined,
): SelfFundAccess {
  if (!clientStripe) return 'none';
  return clientStripe.hasActivePlan ? 'active' : 'expired';
}

export type SelfFundRouteTarget =
  | { kind: 'consumer-step'; step: ConsumerStepId }
  | { kind: 'legacy-paid-page'; consumerDestination: 'entry' | 'self-fund' };

export type SelfFundRouteDecision = { kind: 'allow' } | { kind: 'redirect'; to: string };

export function resolveSelfFundRoute(
  target: SelfFundRouteTarget,
  context: { clientId: string; isConsumerSession: boolean; access: SelfFundAccess },
): SelfFundRouteDecision {
  if (!context.isConsumerSession) return { kind: 'allow' };

  if (target.kind === 'legacy-paid-page') {
    return {
      kind: 'redirect',
      to:
        target.consumerDestination === 'self-fund'
          ? consumerStepPath(context.clientId, 'self-fund')
          : consumerEntryPath(context.clientId),
    };
  }

  if (!isSelfFundSequenceStep(target.step) || context.access === 'active') return { kind: 'allow' };
  return { kind: 'redirect', to: consumerStepPath(context.clientId, 'self-fund') };
}

/** Where "continue" goes in the self-funding sequence: the step the consumer last reached, or its start. */
export function resolveSelfFundResumeStep(lastStep: string | null | undefined): ConsumerStepId {
  return isSelfFundSequenceStep(lastStep) ? lastStep : FIRST_SELF_FUND_SEQUENCE_STEP;
}
